/**
 * Dev-sync J1 — Plotly layout + config for the psychrometric chart.
 * Direct port of legacy buildLayout (psychrometric_chart.js:1541-1589) and
 * the renderChart config literal (js:681-695).
 */

import type { PsyAxes } from './psychrometricApi';
import { DEFAULT_AXES, toGrains } from './chartTraces';

export function buildLayout(axes: PsyAxes | null | undefined): Record<string, unknown> {
  const a = axes || DEFAULT_AXES;
  return {
    // l trimmed to just enough room for the leftmost x-tick label not to
    // clip (the y-axis is on the RIGHT); t trimmed 5px; r/b grown for the
    // tick-label standoff + axis-title breathing room (js:1544-1551).
    margin: { l: 24, r: 110, t: 25, b: 70 },
    xaxis: {
      title: 'Dry-bulb temperature (°F)',
      range: [a.t_db_min, a.t_db_max],
      gridcolor: '#eaeaea',
      zeroline: false,
      tickformat: '.2f',
      // Invisible outside ticks as a spacer: transparent tickcolor, but
      // ticklen still pushes the labels 5px off the axis line (js:1559-1564).
      ticks: 'outside',
      ticklen: 5,
      tickcolor: 'rgba(0,0,0,0)',
    },
    yaxis: {
      title: { text: 'Humidity ratio (gr/lb dry air)', standoff: 25 },
      range: [toGrains(a.w_min), toGrains(a.w_max)],
      side: 'right',
      gridcolor: '#eaeaea',
      zeroline: false,
      tickformat: '.2f',
      ticks: 'outside',
      ticklen: 5,
      tickcolor: 'rgba(0,0,0,0)',
    },
    showlegend: false,
    hovermode: 'closest',
    plot_bgcolor: '#fafafa',
    // Adding a state point (J2) is the only mouse interaction the chart
    // supports — no drag-zoom/pan/box-select; also sidesteps Plotly's
    // click-with-incidental-movement rubber-band-zoom bug (js:1580-1587).
    dragmode: false,
  };
}

/** renderChart's config (js:681-695): doubleClick off (fights the J2
 * dblclick-to-edit and shifts axis ranges under its hit-test); no mode bar,
 * no scroll zoom; PNG export lives in the dialog header (J3a), not Plotly's
 * toolbar. */
export const PLOT_CONFIG = {
  responsive: true,
  displaylogo: false,
  displayModeBar: false,
  scrollZoom: false,
  doubleClick: false,
} as const;
